import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { analyticsService } from "@medbillai/app/lib/analytics";
import { useEnumVariation, useFlagBootstrap } from "@medbillai/app/lib/flag";
import { useFlagHasIdentified } from "@medbillai/app/lib/flag/shared";
import { FlowTimelineTamaguiTransition } from "@medbillai/app/lib/flow-engine/FlowTimelineTamaguiTransition";
import { IntakeRoutingScreen } from "@medbillai/app/screens/intake-routing";
import { OnboardingShellV3 } from "@medbillai/app/screens/onboarding-v3";
import { SignUpOnboardingLayout } from "@medbillai/app/screens/sign-up-onboarding/layout.web";
import { Flag, INTAKE_ROUTING_VARIANTS, stripExperimentVariant } from "@medbillai/utils/flag";
import { withLaunchDarklyFlags } from "../utils/flag-bootstrap";
import { SlidingLayout } from "../utils/transition/SlidingLayout";
import type { NextPageWithLayout } from "./_app";
export const getServerSideProps = withLaunchDarklyFlags();
const ControlArm = () => <OnboardingShellV3 phase="preauth" Transition={FlowTimelineTamaguiTransition} data-sentry-element="OnboardingShellV3" data-sentry-component="ControlArm" data-sentry-source-file="get-started.tsx" />;

// Mounted only for visitors who could actually be served the routing arm:
// reading the flag is what enrols, so this must not run for anyone the page
// has already decided cannot receive it.
const RoutingSplit = () => {
  const variant = useEnumVariation(INTAKE_ROUTING_VARIANTS, Flag.INTAKE_ROUTING, "control");
  const intakeRouting = stripExperimentVariant(variant) === "routing";

  // The warehouse's exposure record — fired for every arm, raw variant
  // intact, mirroring the LD enrollment the evaluation above performed. Held
  // until the value is settled (an unavailable bootstrap serves the default
  // first and re-evaluates after identify), and keyed by the emitted pair so
  // a late correction emits again instead of being suppressed.
  const bootstrap = useFlagBootstrap();
  const hasIdentified = useFlagHasIdentified();
  const settled = bootstrap?.status === "ready" || hasIdentified;
  const armAssignedRef = useRef<string | null>(null);
  useEffect(() => {
    const emitted = `${variant}:${intakeRouting}`;
    if (!settled || armAssignedRef.current === emitted) {
      return;
    }
    armAssignedRef.current = emitted;
    analyticsService.trackEvent("IntakeRoutingArmAssigned", {
      variant,
      routingShown: intakeRouting
    });
  }, [settled, variant, intakeRouting]);
  return intakeRouting ? <IntakeRoutingScreen data-sentry-element="IntakeRoutingScreen" data-sentry-component="RoutingSplit" data-sentry-source-file="get-started.tsx" /> : <ControlArm data-sentry-element="ControlArm" data-sentry-component="RoutingSplit" data-sentry-source-file="get-started.tsx" />;
};
export const Page: NextPageWithLayout = () => {
  const {
    query
  } = useRouter();
  // The funnel's not-Natera exit sends members here to bypass routing, so they
  // can never be served either arm; enrolling them would dilute both.
  const skipRouting = query.skipIntakeRouting === "true";
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="get-started.tsx">
        <title>Get Started</title>
      </Head>
      {skipRouting ? <ControlArm /> : <RoutingSplit />}
    </>;
};
Page.getLayout = page =>
// Both arms paint their own chrome — routing's landing has a wordmark,
// control is a spinner.
<SignUpOnboardingLayout showWordmark={false}>
    <SlidingLayout>{page}</SlidingLayout>
  </SignUpOnboardingLayout>;
export default Page;